import React, { useState, useEffect, useRef } from 'react';
import { useSOWDataStore, useTranslation, useDialogStore, useSOWTasksStore } from '../../store';
import Loading from '../Loading';
import LoadingError from '../LoadingError';
import '../../css/Tasks.css';
import { queryParamsService } from '../../services/queryParams';
import { isValueEmpty, toDateTimeLocalValue, fromDateTimeLocalValue, MS_PER_MINUTE, getMinEndTimeMs } from '../../services/utils';
import { CONSTANTS } from '../../constants/constants';
import { getCurrentLocation } from '../../services/geoLocation';
import { addSOWCompletedTasks } from '../../services/api';
import NoData from '../NoData';
import PartsUsageTask from './PartsUsageTask';
import { isPartsUsageTask } from '../../services/partsUsage';
import { getPictureData, setPictureData, removePictureData, getFileData, setFileData, removeFileData } from '../../services/binaryDataService';

const Picture = ({ picture, imageView, ...props }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    getPictureData(picture).then(setSrc);
  }, [picture]);

  const handleClick = () => {
    if (src && imageView) {
      imageView(src);
    }
  };

  return src ? (
    <img 
      src={src} 
      onClick={handleClick}
      {...props} 
    />
  ) : (
    <div>Loading...</div>
  );
};

const PictureCapture = ({ taskId, data, mandatoryText, disabled, forcePersist, maxEntries = 5 }) => {
  const [pictures, setPictures] = useState(data.value ? data.value : []);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCaptureButton, setShowCaptureButton] = useState(false);
  const [stream, setStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const { alert, imageView } = useDialogStore();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const { t } = useTranslation();

  // Manage body class for camera modal
  useEffect(() => {
    if (showCamera) {
      document.body.classList.add('camera-modal-open');
    } else {
      document.body.classList.remove('camera-modal-open');
    }

    return () => {
      document.body.classList.remove('camera-modal-open');
    };
  }, [showCamera]);

  // Start camera when showCamera becomes true
  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [showCamera]);

  const startCamera = async () => {
    try {
      setIsCapturing(true);
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false 
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert(t('cameraError'));
      setShowCamera(false);
      setIsCapturing(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCapturing(false);
  };

  const capturePicture = (e) => {
    e.stopPropagation();
    if (videoRef.current && canvasRef.current && pictures.length < maxEntries) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const pictureData = canvas.toDataURL('image/jpeg', 0.8);
      const newPicture = {
        id: Date.now(),
        timestamp: new Date().toISOString()
      };
      
      setPictureData(newPicture, pictureData);

      const newPictures = [...pictures, newPicture];
      setPictures(newPictures);
      data.value = newPictures;
      forcePersist();
      setShowCamera(false);
    }
  };

  const handleTakePicture = () => {
    if (pictures.length >= maxEntries) {
      alert(t('maxPicturesReached'));
      return;
    }
    setShowCaptureButton(false);
    setTimeout(() => {
      setShowCaptureButton(true);
    }, 1000); // add a delay to prevent users from capturing black images
    setShowCamera(true);
  };

  const handleUploadPicture = (e) => {
    const files = Array.from(e.target.files || []);
    const availableSlots = maxEntries - pictures.length;
    const filesToProcess = files.slice(0, availableSlots);

    if (files.length > availableSlots) {
      alert(t('maxPicturesExceeded', {"count": availableSlots}));
    }

    filesToProcess.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newPicture = {
          id: Date.now() + index,
          timestamp: new Date().toISOString(),
          fileName: file.name
        };
        
        setPictureData(newPicture, e.target.result);
        
        setPictures(prev => {
          const updatedPictures = [...prev, newPicture];
          data.value = updatedPictures;
          return updatedPictures;
        });
        
        forcePersist();
      };
      reader.readAsDataURL(file);
    });
  };

  const cancelCapture = () => {
    setShowCamera(false);
  };

  const removePicture = (e, picture) => {
    e.stopPropagation()
    const newPictures = pictures.filter(pic => pic.id !== picture.id);
    setPictures(newPictures);
    data.value = newPictures;
    removePictureData(picture);
    forcePersist();
  };

  const getPictureCountText = () => {
    return `(${pictures.length}/${maxEntries})`;
  };
  
  const showPicture = (pictureData) => {
    imageView(pictureData);
  };

  return (
    <div className="picture-capture">
      <div className="measure-header">
        <span>{t('Picture')} {getPictureCountText()}</span>
        <span className="mandatory-text">{mandatoryText}</span>
      </div>
      
      {!disabled && (
          <div className="picture-actions">
            <button 
              onClick={handleTakePicture}
              disabled={isCapturing || pictures.length >= maxEntries}
              className="capture-btn"
            >
              {isCapturing ? t('capturing') : t('takePicture')}
            </button>
            <label className="upload-btn">
              {t('uploadPicture')}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUploadPicture}
                style={{ display: 'none' }}
                disabled={pictures.length >= maxEntries}
              />
            </label>
          </div>
      )}

      {/* Camera View Modal */}
      {showCamera && (
        <div className="camera-modal">
          <div className="camera-container">
            <div className="camera-header">
              <h3>{t('takePicture')} {getPictureCountText()}</h3>
              <button onClick={cancelCapture} className="close-camera-btn">
                ×
              </button>
            </div>
            
            <div className="video-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
            
            {showCaptureButton && (
                <div className="camera-controls">
                  <button onClick={(e) => capturePicture(e)} className="capture-button">
                    <div className="capture-circle"></div>
                  </button>
                </div>
            )}
          </div>
        </div>
      )}

      {/* Pictures Preview */}
      {pictures.length > 0 && (
        <div className="pictures-preview">
          <h4>{t('capturedPictures')} ({pictures.length})</h4>
          <div className="pictures-grid">
            {pictures.map((picture) => (
              <div key={picture.id} className="picture-item">
                <Picture picture={picture} alt={`Captured ${picture.id}`} imageView={imageView}/>
                <div className="picture-info">
                  {picture.fileName && (
                    <span className="picture-filename">{picture.fileName}</span>
                  )}
                  <span className="picture-time">
                    {new Date(picture.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                {!disabled && (
                    <button 
                      onClick={(e) => removePicture(e, picture)}
                      className="remove-btn"
                    >
                      {t('remove')}
                    </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const BooleanInput = ({ taskId, data, mandatoryText, disabled, forcePersist }) => {
    const [value, setValue] = useState(data.value !== undefined ? data.value : null);
    const { t } = useTranslation();

    const handleChange = (selectedValue) => {
        setValue(selectedValue);
        data.value = selectedValue;
        forcePersist();
    };

    return (
        <div className="boolean-input">
            <div className="measure-header">
                <span>{t('boolean')}</span>
                <span className="mandatory-text">{mandatoryText}</span>
            </div>
            <div className="radio-group" role="radiogroup">
                <label className="radio-option">
                    <input
                        disabled={disabled}
                        type="radio"
                        name={`boolean-${taskId}`}
                        checked={value === true}
                        onChange={() => handleChange(true)}
                        className="radio-input"
                    />
                    <span className="radio-label">{t('yes')}</span>
                </label>
                <label className="radio-option">
                    <input
                        disabled={disabled}
                        type="radio"
                        name={`boolean-${taskId}`}
                        checked={value === false}
                        onChange={() => handleChange(false)}
                        className="radio-input"
                    />
                    <span className="radio-label">{t('no')}</span>
                </label>
            </div>
        </div>
    );
};
  
const NumberInput = ({ taskId, data, mandatoryText, disabled, forcePersist }) => {
    const [value, setValue] = useState(data.value ? data.value : '');
    const { t } = useTranslation();

    const handleChange = (e) => {
      const newValue = e.target.value;
      // Allow only numbers and decimal point
      if (/^\d*\.?\d*$/.test(newValue)) {
        setValue(newValue);
        data.value = newValue;
        forcePersist();
      }
    };

    return (
      <div className="number-input">
        <div className="measure-header">
          <span>{t('Number')}</span>
          <span className="mandatory-text">{mandatoryText}</span>
        </div>
        <input
          disabled={disabled}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={t('enterNumber')}
          className="number-field"
        />
      </div>
    );
};

const TextInput = ({ taskId, data, mandatoryText, disabled, forcePersist }) => {
    const [value, setValue] = useState(data.value ? data.value : '');
    const { t } = useTranslation();

    const handleChange = (e) => {
      let newValue = e.target.value;
      setValue(newValue);
      data.value = newValue;
      forcePersist();
    };

    return (
      <div className="text-input">
        <div className="measure-header">
          <span>{t('Text')}</span>
          <span className="mandatory-text">{mandatoryText}</span>
        </div>
        <textarea
          disabled={disabled}
          value={value}
          onChange={handleChange}
          placeholder={t('enterText')}
          className="text-field"
          rows={3}
        />
      </div>
    );
};

const DocumentUpload = ({ 
  taskId, 
  data, 
  mandatoryText,
  disabled,
  forcePersist,
  maxEntries = 5,
  maxFileSize = 5 * 1024 * 1024 // Default 5MB in bytes
}) => {
  const [documents, setDocuments] = useState(data.value ? data.value : []);
  const { alert } = useDialogStore();
  const { t } = useTranslation();

  const readFileAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const availableSlots = maxEntries - documents.length;
    let filesToProcess = files.slice(0, availableSlots);

    // Check for files that exceed size limit
    const oversizedFiles = filesToProcess.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const oversizedNames = oversizedFiles.map(f => f.name).join(', ');
      alert(
        t('fileSizeExceeded', {"maxFileSize": formatFileSize(maxFileSize), "oversizedNames": oversizedNames})
      );

      // Remove oversized files from processing
      filesToProcess = filesToProcess.filter(file => file.size <= maxFileSize);
    }

    if (files.length > availableSlots) {
      alert(t('maxDocumentsExceeded', {"count": availableSlots}));
    }

    // If no valid files remain after size check, return
    if (filesToProcess.length === 0) {
      return;
    }

    try {
      const newDocuments = await Promise.all(
        filesToProcess.map(async (file) => {
          const fileData = await readFileAsArrayBuffer(file);
          
          let newFile = {
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            uploadTime: new Date().toISOString()
          };
          
          setFileData(newFile, Array.from(new Uint8Array(fileData)));
          
          return newFile;
        })
      );

      const updatedDocuments = [...documents, ...newDocuments];
      setDocuments(updatedDocuments);
      data.value = updatedDocuments;
      forcePersist();
      
    } catch (error) {
      alert(t('uploadError'));
    }
  };

  const handleRemoveDocument = (document) => {
    const newDocuments = documents.filter(doc => doc.id !== document.id);
    removeFileData(document);
    setDocuments(newDocuments);
    data.value = newDocuments;
    forcePersist();
  };

  const getDocumentCountText = () => {
    return `(${documents.length}/${maxEntries})`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to get human-readable size limit
  const getSizeLimitText = () => {
    return formatFileSize(maxFileSize);
  };

  return (
    <div className="document-upload">
      <div className="measure-header">
        <span>{t('Document')} {getDocumentCountText()}</span>
        <span className="mandatory-text">{mandatoryText}</span>
      </div>
      <div className="document-actions">
        {!disabled && (
            <label className="upload-btn">
              {t('uploadDocument')}
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={documents.length >= maxEntries}
              />
            </label>
        )}
        <div className="file-size-info">
          {t('maxFileSize', {"maxFileSize": getSizeLimitText()})}
        </div>
      </div>
      
      {documents.length > 0 && (
        <div className="documents-list">
          <h4>{t('uploadedDocuments')} ({documents.length})</h4>
          {documents.map((document) => (
            <div key={document.id} className="document-preview">
              <div className="document-info">
                <span className="document-name">{document.name}</span>
                <span className="document-details">
                  {formatFileSize(document.size)} • {new Date(document.uploadTime).toLocaleTimeString()}
                </span>
              </div>
              {!disabled && (
                  <button 
                    onClick={() => handleRemoveDocument(document)}
                    className="remove-btn"
                  >
                    {t('remove')}
                  </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TasksTab = () => {
  const { t } = useTranslation();
  const { alert } = useDialogStore();
  const { sowData } = useSOWDataStore();
  
  // Get state and actions from tasks store
  const {
    searchTerm,
    sortField,
    sortDirection,
    loading,
    error,
    filteredAndSortedTasks,
    statistics,
    setSearchTerm,
    setSortField,
    setSortDirection,
    initializeTasks,
    handleTimerTick,
    startTask,
    togglePauseTask,
    toggleTaskCompletion,
    updateTaskTime,
    updateTaskDates,
    submitTasks,
    fetchLocation,
    areTasksSubmitted,
    setPartUsageAnswer,
    setPartsUsageNotApplicable,
    persistState
  } = useSOWTasksStore();

  // Initialize tasks
  useEffect(() => {
    queryParamsService.initialize();
    initializeTasks(t);
    handleTimerTick(); // to update timers in case of a tab switch. DO NOT REMOVE
  }, [initializeTasks]);

  // Handle timer tick
  useEffect(() => {
    let timer;
    timer = setInterval(() => {
      handleTimerTick();
    }, 60 * 1000);  // 1 minute to reduce the impact on app 
    return () => clearInterval(timer);
  }, [handleTimerTick]);

  const generateTaskEntry = (taskId, data, disabled) => {
    if (isValueEmpty(data)) return null;
    
    return (
      <div className="task-entry">
        <div dangerouslySetInnerHTML={{ __html: data.description }}/>
        {data.measures && data.measures.length > 0 && (
          <div className="taskMeasures">
            <h6>{t('measurements')}</h6>
            <div className="task-measures-list">
              {getMeasurementsElements(taskId, data, disabled, persistState)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getMeasurementsElements = (taskId, data, disabled, forcePersist) => {
    return data.measures.map((measure, index) => {
      const mandatoryText = measure.mandatory ? ` (${t('mandatory')})` : ` (${t('optional')})`;
      
      switch(measure.name) {
        case 'Picture':
          return (
            <div key={`${taskId}-picture-${index}`} className="task-measures-list-element">
              <PictureCapture taskId={taskId} data={measure} mandatoryText={mandatoryText} disabled={disabled} forcePersist={forcePersist} />
            </div>
          );
        case 'Number':
          return (
            <div key={`${taskId}-number-${index}`} className="task-measures-list-element">
              <NumberInput taskId={taskId} data={measure} mandatoryText={mandatoryText} disabled={disabled} forcePersist={forcePersist} />
            </div>
          );
        case 'Boolean':
          return (
            <div key={`${taskId}-boolean-${index}`} className="task-measures-list-element">
              <BooleanInput taskId={taskId} data={measure} mandatoryText={mandatoryText} disabled={disabled} forcePersist={forcePersist} />
            </div>
          );
        case 'Text':
          return (
            <div key={`${taskId}-text-${index}`} className="task-measures-list-element">
              <TextInput taskId={taskId} data={measure} mandatoryText={mandatoryText} disabled={disabled} forcePersist={forcePersist} />
            </div>
          );
        case 'Document':
          return (
            <div key={`${taskId}-document-${index}`} className="task-measures-list-element">
              <DocumentUpload taskId={taskId} data={measure} mandatoryText={mandatoryText} disabled={disabled} forcePersist={forcePersist} />
            </div>
          );
        default:
          return null;
      }
    });
  };

  const formatLocation = (task) => {
    if (!task.location && !task.locationError) { 
      return t('notCaptured');
    } else if (task.locationError) {
      return `<span class="error-text">${t('error')} ${task.locationError}</span>`;
    }
    return `${t('lat')} ${task.location.latitude.toFixed(6)}, ${t('lng')} ${task.location.longitude.toFixed(6)} (${t('accuracy')} ${task.location.accuracy.toFixed(2)}m)`;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes + 1}m`;
    } else if (minutes > 0) {
      if (secs > 0) {
        return `${minutes + 1}m`;
      }
      return `${minutes}m`;
    } else {
      return `${1}m`;
    }
  };
  
  const getStatusClass = (status) => {
    switch (status) {
      case CONSTANTS.STATUS.COMPLETED: return 'completed-task';
      case CONSTANTS.STATUS.IN_PROGRESS: return 'in-progress-task';
      default: return '';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case CONSTANTS.STATUS.COMPLETED: return '#155724';
      case CONSTANTS.STATUS.IN_PROGRESS: return '#856404';
      case CONSTANTS.STATUS.NOT_STARTED: return '#6c757d';
      case CONSTANTS.STATUS.PAUSED: return '#ffa500';
      default: return '#6c757d';
    }
  };
  
  const getPauseButton = (task) => {
    return (
      <button onClick={() => togglePauseTask(task.id)} className="pause-btn">
        {!task.paused ? (
          <><span className="glyphicon glyphicon-pause"/> {t('pause')}</>
        ) : (
          <><span className="glyphicon glyphicon-play"/> {t('resume')}</>
        )}
      </button>
    );
  };
  
  const getStatusTag = (task) => {
    let status = task.status;

    if (task.paused) {
        status = CONSTANTS.STATUS.PAUSED 
    } else if (task.submitted) {
        status = CONSTANTS.STATUS.SUBMITTED
    }
    
    return (
      <span className="task-status" style={{ color: getStatusTextColor(status) }}>
        {t(status)}
      </span>
    );
  };

  // Loading and error states
  if (loading) {
    return <Loading message={t('loadingTasks')}/>;
  }

  if (error) {
    return <LoadingError message={t('errorLoadingTasks', { error })}/>;
  }

  const stats = statistics();
  const displayedTasks = filteredAndSortedTasks();
  
  if (!sowData) {
    return (
      <NoData/>
    );
  }

  return (
    <div className="tab-content tasks-tab">
      {/* Your existing JSX remains the same, just replace state variables with store ones */}
      <div className="tasks-header">
        <h2>{t('taskManagement')}</h2>
        <p>{t('workOrder')} {sowData?.workorderKey || t('na')}</p>
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <h3>{t('totalTasks')}</h3>
          <span className="stat-number">{stats.totalTasks}</span>
        </div>
        <div className="stat-card">
          <h3>{t('completed')}</h3>
          <span className="stat-number">{stats.completedTasks}</span>
        </div>
        <div className="stat-card">
          <h3>{t('inProgess')}</h3>
          <span className="stat-number">{stats.inProgressTasks}</span>
        </div>
        <div className="stat-card">
          <h3>{t('completion')}</h3>
          <span className="stat-number">{stats.completionPercentage}%</span>
        </div>
        <div className="stat-card">
          <h3>{t('totalTime')}</h3>
          <span className="stat-number">{formatTime(stats.totalTimeSpent)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="tasks-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('searchTask')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="sort-controls">
          <label htmlFor="sort-field">{t('sortBy')}</label>
          <select
            id="sort-field"
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="sort-select"
          >
            <option value="order">{t('order')}</option>
            <option value="description">{t('description')}</option>
            <option value="status">{t('statuso')}</option>
            <option value="timeSpent">{t('timeSpent')}</option>
          </select>
          
          <button
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="sort-direction-btn"
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="tasks-list">
        {displayedTasks.length === 0 ? (
          <div className="no-tasks">
            {searchTerm ? t('notTaskFound') : t('notTaskAvailable')}
          </div>
        ) : (
          displayedTasks.map((task) => (
            <div
              key={task.id}
              className={`task-item ${getStatusClass(task.status)}`}
              style={{                
                borderLeft: `4px solid ${getStatusTextColor(task.status)}`
              }}
            >
              <div className="task-title">{task.id}.</div>   
              <div className="task-main">
                <div className="task-info">
                  {isPartsUsageTask(task) ? (
                    <PartsUsageTask
                      task={task}
                      disabled={task.submitted}
                      onAnswer={(lineKey, answer) => setPartUsageAnswer(task.id, lineKey, answer)}
                      onNotApplicable={(isChecked) => setPartsUsageNotApplicable(task.id, isChecked)}
                      t={t}
                    />
                  ) : generateTaskEntry(task.id, task.taskData, task.submitted || task.completed)}
                  <div className="task-meta">
                    {getStatusTag(task)}
                    {task.timeSpent > 0 && (
                      <span className="task-time">
                        {t('time')} {formatTime(task.timeSpent)}
                      </span>
                    )}
                  </div>
                </div>
                {!(task.submitted) && !isPartsUsageTask(task) && (
                    <div className="task-actions">
                      {task.status === CONSTANTS.STATUS.IN_PROGRESS ? (
                        <>
                          {getPauseButton(task)}
                        </>
                      ) : (
                        <button
                          onClick={() => startTask(task.id, t)}
                          disabled={task.status === CONSTANTS.STATUS.COMPLETED}
                          className="start-btn"
                        >
                          <span className="glyphicon glyphicon-play"></span> {t('start')}
                        </button>
                      )}
                      
                      <button
                        onClick={() => toggleTaskCompletion(task.id, t)}
                        className="complete-btn"
                      >
                        {task.status === CONSTANTS.STATUS.COMPLETED ? (
                          <>
                            <span className="glyphicon glyphicon-repeat"></span> {t('undo')}
                          </>
                        ) : (
                          <>
                            <span className="glyphicon glyphicon-ok"></span> {t('complete')}
                          </>
                        )}
                      </button>
                      
                      <div className="time-controls">
                        <button
                          onClick={() => updateTaskTime(task.id, -60)}
                          disabled={task.status === CONSTANTS.STATUS.COMPLETED}
                          className="time-adjust-btn"
                        >
                          -1m
                        </button>
                        <button
                          onClick={() => updateTaskTime(task.id, 60)}
                          disabled={task.status === CONSTANTS.STATUS.COMPLETED}
                          className="time-adjust-btn"
                        >
                          +1m
                        </button>
                      </div>

                      {task.startTime && (
                        <div className="date-controls">
                          <label className="date-control">
                            <span>{t('startDate')}</span>
                            <input
                              type="datetime-local"
                              value={toDateTimeLocalValue(task.startTime)}
                              max={toDateTimeLocalValue(new Date(Math.min(Date.now(), task.completed && task.endTime ? new Date(task.endTime).getTime() - MS_PER_MINUTE : Date.now())).toISOString())}
                              onChange={(e) => {
                                const iso = fromDateTimeLocalValue(e.target.value);
                                if (iso) updateTaskDates(task.id, { startTime: iso }, t);
                              }}
                            />
                          </label>
                          {task.completed && task.endTime && (
                            <label className="date-control">
                              <span>{t('completedDate')}</span>
                              <input
                                type="datetime-local"
                                value={toDateTimeLocalValue(task.endTime)}
                                min={toDateTimeLocalValue(new Date(getMinEndTimeMs(new Date(task.startTime).getTime())).toISOString())}
                                max={toDateTimeLocalValue(new Date().toISOString())}
                                onChange={(e) => {
                                  const iso = fromDateTimeLocalValue(e.target.value);
                                  if (iso) updateTaskDates(task.id, { endTime: iso }, t);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </div>
              
              {task.startTime && (
                <div className="task-timestamps">
                  <small>
                    {t('started')} {new Date(task.startTime).toLocaleString()}
                    {task.endTime && ` • ${t('completedC')} ${new Date(task.endTime).toLocaleString()}`}
                    {task.location && ` • ${t('location')} ${formatLocation(task)}`}
                  </small>
                </div>
              )}
            </div>
          ))
        )}
        {!areTasksSubmitted() && (
            <button onClick={() => submitTasks(t)} className="complete-btn">
              <span className="glyphicon glyphicon-send"></span> {t('submit')}
            </button>
        )}
      </div>
    </div>
  );
};

export default TasksTab;